import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { LOGIN_ROUTE, defaultRoute } from '../../config/menuData.js';

/**
 * The layouts' session chrome, driven by the app's auth state.
 *
 * The raw shells hard-code a signed-in strip:
 *
 *   <div class="ml-auto d-flex align-items-center">
 *     <span class="mr-3">Hello, User!</span>
 *     <img src="https://placehold.co/30" alt="User Avatar" class="rounded-circle">
 *   </div>
 *
 * That structure is preserved (Bootstrap 5 spacing names, `ms-auto`/`me-3`), with
 * the placeholder text bound to the real session and the required controls added:
 *
 *  * authenticated  → the user's name + avatar, and an account menu with **log out**
 *  * anonymous      → a **log in** entry
 *
 * The avatar renders the user's image when the profile has one, otherwise their
 * initials — no request to the design's external placeholder service.
 */

function initialsOf(user) {
  const source = (user?.name || user?.email || '').trim();
  if (!source) return '?';
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  return (parts[0][0] + (parts[1] ? parts[1][0] : '')).toUpperCase();
}

function Avatar({ user }) {
  if (user?.avatarUrl) {
    return (
      <img
        className="rounded-circle app-account__avatar"
        src={user.avatarUrl}
        alt="User Avatar"
        width="30"
        height="30"
      />
    );
  }

  return (
    <span className="rounded-circle app-account__avatar" aria-hidden="true">
      {initialsOf(user)}
    </span>
  );
}

export default function AccountChrome({ className = 'ms-auto d-flex align-items-center' }) {
  const { isAuthenticated, user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Anonymous: the login affordance the layout's public pages need.
  if (!isAuthenticated) {
    return (
      <div className={className}>
        <Link className="btn btn-sm btn-outline-light" to={LOGIN_ROUTE}>
          <i className="fa fa-sign-in-alt me-2" aria-hidden="true" />
          Log in
        </Link>
      </div>
    );
  }

  const displayName = user?.name || user?.email || 'User';
  const firstName = displayName.split(/[\s@]+/)[0];

  const handleLogout = () => {
    setOpen(false);
    logout();
    navigate(LOGIN_ROUTE, { replace: true });
  };

  return (
    <div className={`${className} dropdown`} ref={ref}>
      <span className="me-2 app-account__greeting" title={displayName}>{firstName}</span>
      <button
        type="button"
        id="accountMenu"
        className="btn btn-link p-0 border-0 d-inline-flex align-items-center"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Account menu for ${displayName}`}
        onClick={() => setOpen((value) => !value)}
      >
        <Avatar user={user} />
      </button>
      <ul
        className={`dropdown-menu dropdown-menu-end${open ? ' show' : ''}`}
        aria-labelledby="accountMenu"
      >
        <li>
          <h6 className="dropdown-header text-truncate">{user?.email || displayName}</h6>
        </li>
        <li>
          <hr className="dropdown-divider" />
        </li>
        <li>
          <Link className="dropdown-item" to={defaultRoute()} onClick={() => setOpen(false)}>
            <i className="fa fa-th-large me-2" aria-hidden="true" />
            My workspace
          </Link>
        </li>
        <li>
          {/* Each user manages their own second factor — account-level, not an
              admin screen, so it belongs in the account menu. */}
          <Link className="dropdown-item" to="/security" onClick={() => setOpen(false)}>
            <i className="fa fa-shield-halved me-2" aria-hidden="true" />
            Security
          </Link>
        </li>
        <li>
          <button type="button" className="dropdown-item" onClick={handleLogout}>
            <i className="fa fa-sign-out-alt me-2" aria-hidden="true" />
            Log out
          </button>
        </li>
      </ul>
    </div>
  );
}
